import BreadCrumb from "Components/Common/BreadCrumb";
import CustomButton from "Components/Custom/CustomButton";
import CustomInput from "Components/Custom/CustomInput";
import { useFormik } from "formik";
import { changePasswordApi } from "helpers/ApiManager";
import { Card, CardBody, Col, Container, Row } from "reactstrap";
import { ErrorHandler, SuccessToast } from "utils/toast";
import * as Yup from "yup";
import {
  VALIDATION_MESSAGES,
  VALIDATION_LIMITS,
} from "Components/constants/validation";
import { useState } from "react";

interface ChangePasswordValues {
  oldPassword: "";
  newPassword: "";
  confirmPassword: "";
}

const ChangePassword = () => {
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const validation = useFormik<ChangePasswordValues>({
    enableReinitialize: true,
    initialValues: {
      oldPassword: "",
      newPassword: "",
      confirmPassword: "",
    },
    validationSchema: Yup.object({
      oldPassword: Yup.string()
        .required(VALIDATION_MESSAGES.REQUIRED("Old Password"))
        .test(
          "no-spaces",
          "Password cannot contain spaces",
          (value) => !value || !/\s/.test(value)
        ),
      newPassword: Yup.string()
        .required(VALIDATION_MESSAGES.REQUIRED("New Password"))
        .test(
          "no-spaces",
          "Password cannot contain spaces",
          (value) => !value || !/\s/.test(value)
        )
        .min(
          VALIDATION_LIMITS.PASSWORD.MIN,
          VALIDATION_MESSAGES.MIN_LENGTH(
            "New Password",
            VALIDATION_LIMITS.PASSWORD.MIN
          )
        )
        .max(
          VALIDATION_LIMITS.PASSWORD.MAX,
          VALIDATION_MESSAGES.MAX_LENGTH(
            "New Password",
            VALIDATION_LIMITS.PASSWORD.MAX
          )
        ),

      confirmPassword: Yup.string()
        .required(VALIDATION_MESSAGES.REQUIRED("Confirm Password"))
        .oneOf([Yup.ref("newPassword")], "Passwords must match")
        .test(
          "no-spaces",
          "Password cannot contain spaces",
          (value) => !value || !/\s/.test(value)
        )
        .min(
          VALIDATION_LIMITS.PASSWORD.MIN,
          VALIDATION_MESSAGES.MIN_LENGTH(
            "Confirm Password",
            VALIDATION_LIMITS.PASSWORD.MIN
          )
        )
        .max(
          VALIDATION_LIMITS.PASSWORD.MAX,
          VALIDATION_MESSAGES.MAX_LENGTH(
            "Confirm Password",
            VALIDATION_LIMITS.PASSWORD.MAX
          )
        ),
    }),
    onSubmit: async (values) => {
      console.log(values);
      try {
        setIsLoading(true);
        const res = await changePasswordApi({
          old_password: values.oldPassword?.trim(),
          new_password: values.newPassword?.trim(),
        });
        SuccessToast(res.message);
        validation.resetForm();
      } catch (error) {
        ErrorHandler(error);
      } finally {
        setIsLoading(false);
      }
    },
  });

  document.title = "Change Password | EaziQuote";

  return (
    <div className="page-content">
      <Container fluid>
        <BreadCrumb
          title="Change Password"
          pageTitle="Settings"
          url="/settings"
        />
        <Card className="bg-white br-10">
          <CardBody className="p-4 mt-0 pt-0">
            <form
              onSubmit={(e) => {
                e.preventDefault();
                validation.handleSubmit();
              }}
              action="#"
            >
              <Row>
                <Col md="12" className="mt-4">
                  <CustomInput
                    label="Old Password"
                    placeholder="Enter old password"
                    type="password"
                    name="oldPassword"
                    value={validation.values.oldPassword}
                    onChange={validation.handleChange}
                    onBlur={validation.handleBlur}
                    touched={validation.touched.oldPassword}
                    error={validation.errors.oldPassword}
                    isPassword
                  />

                  <CustomInput
                    label="New Password"
                    placeholder="Enter new password"
                    type="password"
                    name="newPassword"
                    value={validation.values.newPassword}
                    onChange={validation.handleChange}
                    onBlur={validation.handleBlur}
                    touched={validation.touched.newPassword}
                    error={validation.errors.newPassword}
                    isPassword
                  />

                  <CustomInput
                    label="Confirm New Password"
                    placeholder="Enter confirm password"
                    type="password"
                    name="confirmPassword"
                    value={validation.values.confirmPassword}
                    onChange={validation.handleChange}
                    onBlur={validation.handleBlur}
                    touched={validation.touched.confirmPassword}
                    error={validation.errors.confirmPassword}
                    isPassword
                  />

                  <Col md="12" className="mt-4 pt-2">
                    <CustomButton
                      text="Change Password"
                      type="submit"
                      className="bg-primary br-7"
                      loading={isLoading}
                    />
                  </Col>
                </Col>
              </Row>
            </form>
          </CardBody>
        </Card>
      </Container>
    </div>
  );
};

export default ChangePassword;
